import React, { useRef, useState, useEffect } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';

// ── Inline HP editor ──────────────────────────────────────────────────────────
function HpEditor({ token, updateToken, removeFromInitiative }) {
  const [open,  setOpen]  = useState(false);
  const [input, setInput] = useState('');

  const apply = async (isDamage) => {
    const n = parseInt(input, 10);
    if (!n || n <= 0) return;
    const curHp = token.hp ?? 0;
    const newHp = isDamage
      ? Math.max(0, curHp - n)
      : Math.min(token.maxHp ?? curHp, curHp + n);
    const updates = { hp: newHp };
    if (newHp <= 0 && token.type !== 'player') {
      updates.defeated = true;
      removeFromInitiative(token.id);
    } else if (newHp <= 0 && token.type === 'player' && isDamage) {
      if (curHp <= 0 && token.deathSaves) {
        const ds = token.deathSaves;
        updates.deathSaves = { successes: ds.successes, failures: Math.min(3, ds.failures + 2) };
      } else if (curHp > 0) {
        updates.deathSaves = { successes: 0, failures: 0 };
      }
    } else if (newHp > 0) {
      if (token.defeated) updates.defeated = false;
      if (token.deathSaves) updates.deathSaves = null;
    }
    await updateToken(token.id, updates);
    setInput('');
    setOpen(false);
  };

  if (!open) {
    return (
      <button style={styles.hpPill} onClick={() => setOpen(true)} title="Adjust HP">
        <span style={{ color: hpColour(token.hp, token.maxHp) }}>{token.hp}</span>
        <span style={styles.hpMax}>/{token.maxHp}</span>
      </button>
    );
  }

  return (
    <div style={styles.hpPopup}>
      <input
        autoFocus
        style={styles.hpPopupInput}
        type="number" min="1"
        placeholder="amt"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') apply(true);
          if (e.key === 'Escape') setOpen(false);
        }}
      />
      <button style={styles.dmgBtn}  onClick={() => apply(true)}  title="Damage">⚔</button>
      <button style={styles.healBtn} onClick={() => apply(false)} title="Heal">+</button>
      <button style={styles.cancelHpBtn} onClick={() => setOpen(false)}>✕</button>
    </div>
  );
}

function hpColour(hp, max) {
  const pct = max > 0 ? hp / max : 1;
  if (pct > 0.5) return '#5cb870';
  if (pct > 0.25) return '#d4943a';
  return '#c0392b';
}

// ── Turn timer controls ────────────────────────────────────────────────────
function TimerControls({ initiative, saveInitiative }) {
  const timer = initiative.turnTimer || {};
  const { enabled = false, seconds = 60, startedAt = null, running = false } = timer;
  const [remaining, setRemaining] = useState(seconds);
  const beeped = useRef(false);

  useEffect(() => {
    if (!enabled || !running || !startedAt) {
      setRemaining(timer.remaining ?? seconds);
      return;
    }
    const tick = () => {
      const elapsed = (Date.now() - startedAt) / 1000;
      const r = Math.max(0, seconds - elapsed);
      setRemaining(r);
      if (r <= 10 && r > 0 && !beeped.current) {
        beeped.current = true;
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.type = 'sine'; osc.frequency.value = 660;
          gain.gain.setValueAtTime(0.2, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
          osc.start(); osc.stop(ctx.currentTime + 0.15);
        } catch (e) {}
      }
      if (r <= 0) {
        saveInitiative({ ...initiative, turnTimer: { ...timer, running: false, remaining: 0 } });
      }
    };
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [enabled, running, startedAt, seconds]);

  useEffect(() => { beeped.current = false; }, [startedAt]);

  const toggle = () => {
    if (!enabled) {
      saveInitiative({ ...initiative, turnTimer: { enabled: true, seconds: 60, startedAt: null, running: false, remaining: 60 } });
    } else {
      saveInitiative({ ...initiative, turnTimer: { enabled: false, seconds: 60, startedAt: null, running: false, remaining: 60 } });
    }
  };

  const setDuration = (s) => {
    saveInitiative({ ...initiative, turnTimer: { ...timer, seconds: s, startedAt: null, running: false, remaining: s } });
  };

  const startPause = () => {
    if (running) {
      // Pause — save remaining
      const elapsed = startedAt ? (Date.now() - startedAt) / 1000 : 0;
      const r = Math.max(0, seconds - elapsed);
      saveInitiative({ ...initiative, turnTimer: { ...timer, running: false, remaining: r, startedAt: null } });
    } else {
      // Start — compute startedAt from remaining
      const r = timer.remaining ?? seconds;
      const now = Date.now();
      saveInitiative({ ...initiative, turnTimer: { ...timer, running: true, startedAt: now - (seconds - r) * 1000, remaining: r } });
    }
  };

  const reset = () => {
    saveInitiative({ ...initiative, turnTimer: { ...timer, running: false, startedAt: null, remaining: seconds } });
  };

  if (!enabled) {
    return (
      <button className="btn-ghost btn-sm" style={{ marginTop: 6, width: '100%', fontSize: 11, color: '#6a5d45' }} onClick={toggle}>
        ⏱ Enable Timer
      </button>
    );
  }

  const pct = seconds > 0 ? remaining / seconds : 0;
  const mins = Math.floor(remaining / 60);
  const secs = Math.floor(remaining % 60);
  const display = `${mins}:${secs.toString().padStart(2, '0')}`;
  const isLow = remaining <= 10 && remaining > 0;

  return (
    <div style={timerStyles.container}>
      <div style={timerStyles.header}>
        <span style={timerStyles.label}>⏱ Timer</span>
        <span style={{ ...timerStyles.time, color: isLow ? '#f87171' : '#f0e6d0', animation: isLow && running ? 'pulse 0.5s infinite' : 'none' }}>{display}</span>
        <button style={timerStyles.closeBtn} onClick={toggle} title="Disable timer">✕</button>
      </div>
      <div style={timerStyles.barOuter}>
        <div style={{ ...timerStyles.barFill, width: `${pct * 100}%`, background: isLow ? '#f87171' : '#4ade80' }} />
      </div>
      <div style={timerStyles.btnRow}>
        {[60, 90, 120].map((s) => (
          <button key={s} style={{ ...timerStyles.durBtn, ...(seconds === s ? { borderColor: '#d4a847', color: '#d4a847' } : {}) }} onClick={() => setDuration(s)}>
            {s}s
          </button>
        ))}
      </div>
      <div style={timerStyles.btnRow}>
        <button style={{ ...timerStyles.ctrlBtn, background: running ? '#3a1518' : '#1a3a24', borderColor: running ? '#c0392b' : '#4ade80', color: running ? '#c0392b' : '#4ade80' }} onClick={startPause}>
          {running ? '⏸ Pause' : '▶ Start'}
        </button>
        <button style={timerStyles.ctrlBtn} onClick={reset}>↺ Reset</button>
      </div>
    </div>
  );
}

const timerStyles = {
  container:  { marginTop: 8, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 6, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 },
  header:     { display: 'flex', alignItems: 'center', gap: 6 },
  label:      { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9a8b6e', flex: 1 },
  time:       { fontSize: 18, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", minWidth: 50, textAlign: 'center' },
  closeBtn:   { background: 'transparent', border: 'none', color: '#6a5d45', fontSize: 12, cursor: 'pointer', padding: '0 2px' },
  barOuter:   { height: 4, background: '#1a2a48', borderRadius: 2, overflow: 'hidden' },
  barFill:    { height: '100%', borderRadius: 2, transition: 'width 0.2s' },
  btnRow:     { display: 'flex', gap: 4 },
  durBtn:     { flex: 1, height: 24, background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 3, color: '#9a8b6e', fontSize: 10, fontWeight: 700, cursor: 'pointer', padding: 0 },
  ctrlBtn:    { flex: 1, height: 28, background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0 },
};

export default function InitiativeTracker({ isDm, selectedTokenId, onSelectToken, onHoverToken, combatSelecting: selecting, setCombatSelecting: setSelecting, combatSelectedIds: selectedIds, setCombatSelectedIds: setSelectedIds }) {
  const hoverProps = (tokenId) => onHoverToken ? {
    onMouseEnter: () => onHoverToken(tokenId),
    onMouseLeave: () => onHoverToken(null),
  } : {};
  const {
    tokens, initiative,
    startRolling, submitRoll, submitDiceRoll, beginBattle,
    nextTurn, prevTurn, saveInitiative,
    addToInitiative, removeFromInitiative, clearInitiative, cancelInitiative,
    updateToken, removeToken,
  } = useCampaign();

  // Broadcast a monster's initiative d20 to the dice overlay so the Display
  // animates and the log records it. Tinted DM gold; named after the monster.
  const broadcastMonsterRoll = (tokenId, d20) => {
    const t = tokens[tokenId];
    if (!t) return;
    const bonus = t.initiativeBonus || 0;
    const crit = d20 === 20 ? 'crit' : d20 === 1 ? 'fumble' : null;
    submitDiceRoll({
      playerId:   null,
      playerName: t.name || 'Monster',
      colour:     '#d4a847',
      isDm:       true,
      dice:       [{ sides: 20, results: [d20], kept: [d20] }],
      modifier:   bonus,
      mode:       'normal',
      total:      d20 + bonus,
      crit,
      label:      'Initiative',
    });
  };

  const { phase = 'inactive', turnOrder = [], activeIndex = 0, round = 1, pendingRolls = {} } = initiative;
  const inCombat  = phase === 'active'  && turnOrder.length > 0;
  const isRolling = phase === 'rolling';

  // Local roll inputs for DM (monster rolls)
  const [dmRolls, setDmRolls] = useState({});

  const allCombatTokens = Object.values(tokens).filter((t) => !t.defeated && t.type !== 'object');

  const handleStartSelecting = () => {
    setSelectedIds({});
    setSelecting(true);
  };

  const handleConfirmSelection = () => {
    const ids = Object.entries(selectedIds).filter(([, v]) => v).map(([id]) => id);
    if (ids.length === 0) return;
    setSelecting(false);
    startRolling(ids);
  };

  const handleAutoRollSelected = () => {
    const ids = Object.entries(selectedIds).filter(([, v]) => v).map(([id]) => id);
    if (ids.length === 0) return;
    setSelecting(false);
    // Auto-roll for selected tokens
    const entries = ids
      .map((id) => ({ tokenId: id, roll: Math.floor(Math.random() * 20) + 1 + (tokens[id]?.initiativeBonus || 0) }))
      .sort((a, b) => b.roll - a.roll);
    saveInitiative({ phase: 'active', pendingRolls: {}, turnOrder: entries.map((e) => e.tokenId), activeIndex: 0, round: 1, mode: 'roll', battleScreen: false });
  };

  // Drag reorder state
  const dragIdx  = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const notInOrder = isDm
    ? Object.values(tokens).filter((t) => !turnOrder.includes(t.id) && !t.defeated && t.type !== 'object')
    : [];

  // ── Drag handlers ──────────────────────────────────────────────────────
  const handleDragStart = (e, idx) => { dragIdx.current = idx; e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver  = (e, idx) => { e.preventDefault(); setDragOver(idx); };
  const handleDragEnd   = () => { setDragOver(null); dragIdx.current = null; };

  const handleDrop = async (e, targetIdx) => {
    e.preventDefault();
    setDragOver(null);
    const fromIdx = dragIdx.current;
    if (fromIdx === null || fromIdx === targetIdx) return;
    const newOrder = [...turnOrder];
    const [moved]  = newOrder.splice(fromIdx, 1);
    newOrder.splice(targetIdx, 0, moved);
    const activeTokenId  = turnOrder[activeIndex];
    const newActiveIndex = newOrder.indexOf(activeTokenId);
    await saveInitiative({ ...initiative, turnOrder: newOrder, activeIndex: newActiveIndex });
    dragIdx.current = null;
  };

  // ── DM submits monster roll ────────────────────────────────────────────
  const handleDmRollSubmit = async (tokenId) => {
    const val = dmRolls[tokenId];
    if (!val && val !== 0) return;
    const d20 = parseInt(val, 10);
    if (Number.isFinite(d20)) broadcastMonsterRoll(tokenId, d20);
    await submitRoll(tokenId, val);
    setDmRolls((prev) => { const n = { ...prev }; delete n[tokenId]; return n; });
  };

  // ── Begin battle — submit any outstanding DM rolls first ───────────────
  const handleBeginBattle = async () => {
    // Auto-submit any filled DM roll inputs
    for (const [tokenId, val] of Object.entries(dmRolls)) {
      if (val !== '' && val !== undefined) await submitRoll(tokenId, val);
    }
    await beginBattle();
  };

  // ── Counts for the rolling phase status ───────────────────────────────
  const allTokenIds    = Object.keys(pendingRolls);
  const rolledCount    = allTokenIds.filter((id) => pendingRolls[id] !== null).length;
  const totalCount     = allTokenIds.length;
  const playerTokenIds = allTokenIds.filter((id) => tokens[id]?.type === 'player');
  const monsterIds     = allTokenIds.filter((id) => tokens[id] && tokens[id].type !== 'player');

  // ── ROLLING PHASE — DM view ────────────────────────────────────────────
  if (isRolling && isDm) {
    return (
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.title}>Rolling Initiative</span>
          <span style={styles.round}>{rolledCount}/{totalCount} rolled</span>
        </div>

        <div style={styles.rollList}>
          {/* Players */}
          {playerTokenIds.length > 0 && (
            <div style={styles.rollGroup}>
              <div style={styles.rollGroupLabel}>Players</div>
              {playerTokenIds.map((tokenId) => {
                const token  = tokens[tokenId];
                const rolled = pendingRolls[tokenId];
                if (!token) return null;
                return (
                  <div key={tokenId} style={styles.rollEntry}>
                    <span style={{ ...styles.dot, background: token.colour || '#888' }} />
                    <span style={styles.rollName}>{token.name}</span>
                    {rolled !== null && rolled !== undefined ? (
                      <span style={styles.rolledBadge}>
                        {rolled.roll} {token.initiativeBonus ? `+${token.initiativeBonus}` : ''} = <strong>{rolled.total}</strong>
                      </span>
                    ) : (
                      <span style={styles.waitingBadge}>waiting…</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Monsters / NPCs */}
          {monsterIds.length > 0 && (
            <div style={styles.rollGroup}>
              <div style={styles.rollGroupLabel}>Monsters & NPCs</div>
              {monsterIds.map((tokenId) => {
                const token  = tokens[tokenId];
                const rolled = pendingRolls[tokenId];
                if (!token) return null;
                return (
                  <div key={tokenId} style={styles.rollEntry}>
                    <span style={{ ...styles.dot, background: token.colour || '#888' }} />
                    <span style={styles.rollName}>{token.name}</span>
                    {rolled !== null && rolled !== undefined ? (
                      <span style={styles.rolledBadge}>
                        {rolled.roll}{token.initiativeBonus ? `+${token.initiativeBonus}` : ''} = <strong>{rolled.total}</strong>
                      </span>
                    ) : (
                      <div style={styles.dmRollInput}>
                        <input
                          style={styles.rollInput}
                          type="number"
                          min="1"
                          max="20"
                          placeholder="d20"
                          value={dmRolls[tokenId] ?? ''}
                          onChange={(e) => setDmRolls((p) => ({ ...p, [tokenId]: e.target.value }))}
                          onKeyDown={(e) => e.key === 'Enter' && handleDmRollSubmit(tokenId)}
                        />
                        <button
                          style={styles.submitRollBtn}
                          onClick={() => handleDmRollSubmit(tokenId)}
                          disabled={!dmRolls[tokenId] && dmRolls[tokenId] !== 0}
                        >✓</button>
                        <button
                          style={styles.autoRollBtn}
                          onClick={() => {
                            const d20 = Math.floor(Math.random() * 20) + 1;
                            broadcastMonsterRoll(tokenId, d20);
                            submitRoll(tokenId, d20);
                          }}
                          title="Auto-roll"
                        >🎲</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={styles.controls}>
          <button
            className="btn-primary w-full"
            style={{ fontSize: 16, padding: '12px 0', letterSpacing: '0.04em' }}
            onClick={handleBeginBattle}
          >
            ⚔ Begin Battle
          </button>
          <button className="btn-ghost btn-sm w-full" style={{ marginTop: 6 }} onClick={cancelInitiative}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── ACTIVE COMBAT ──────────────────────────────────────────────────────
  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <span style={styles.title}>Initiative</span>
        {inCombat && <span style={styles.round}>Round {round}</span>}
      </div>

      {selecting ? (
        <div style={styles.selectBody}>
          <div style={styles.selectHeader}>Click tokens on map to add/remove</div>
          <div style={styles.selectCount}>
            {Object.values(selectedIds).filter(Boolean).length} combatants selected
          </div>
          <div style={styles.selectListBody}>
            {allCombatTokens.filter((t) => selectedIds[t.id]).length === 0 ? (
              <p style={styles.empty}>Click tokens on the map to add them.</p>
            ) : (
              allCombatTokens.filter((t) => selectedIds[t.id]).map((t) => (
                <div key={t.id} style={styles.selectRow} onClick={() => setSelectedIds((p) => ({ ...p, [t.id]: false }))} {...hoverProps(t.id)}>
                  <span style={{ ...styles.dot, background: t.colour || '#888' }} />
                  <span style={styles.selectName}>{t.name}</span>
                  <span style={styles.selectRemove}>✕</span>
                </div>
              ))
            )}
          </div>
        </div>
      ) : inCombat ? (
        <ol style={styles.list}>
          {turnOrder.map((tokenId, idx) => {
            const token  = tokens[tokenId];
            const active = idx === activeIndex;
            if (!token) return null;
            return (
              <li
                key={tokenId}
                style={{
                  ...styles.entry,
                  ...(active ? styles.activeEntry : {}),
                  ...(tokenId === selectedTokenId && !active ? styles.selectedEntry : {}),
                  ...(dragOver === idx && dragIdx.current !== idx ? styles.dragOverEntry : {}),
                  opacity: dragIdx.current === idx ? 0.4 : 1,
                }}
                draggable={isDm}
                {...hoverProps(tokenId)}
                onClick={isDm && onSelectToken ? () => onSelectToken(tokenId) : undefined}
                onDragStart={isDm ? (e) => handleDragStart(e, idx) : undefined}
                onDragOver={isDm ? (e) => handleDragOver(e, idx) : undefined}
                onDrop={isDm ? (e) => handleDrop(e, idx) : undefined}
                onDragEnd={isDm ? handleDragEnd : undefined}
              >
                {isDm && <span style={styles.dragHandle}>⠿</span>}
                <span style={{ ...styles.dot, background: token.colour || '#888' }} />
                <span style={styles.posNum}>{idx + 1}</span>
                <span style={styles.entryName}>
                  {token.name}
                  {token.defeated && <span style={styles.defeatedBadge}> ☠</span>}
                </span>
                {active && <span style={styles.activePip}>▶</span>}
                {isDm && <HpEditor token={token} updateToken={updateToken} removeFromInitiative={removeFromInitiative} />}
                {isDm && token.defeated && (
                  <button style={styles.removeMapBtn} onClick={() => removeToken(tokenId)} title="Remove from map">🗑</button>
                )}
                {isDm && (
                  <button style={styles.removeBtn} onClick={() => removeFromInitiative(tokenId)} title="Remove from initiative">×</button>
                )}
              </li>
            );
          })}
        </ol>
      ) : (
        <p style={styles.empty}>
          {isDm ? 'No active combat. Click Start Combat to select combatants.' : 'No active combat.'}
        </p>
      )}

      {isDm && (
        <div style={styles.controls}>
          {!inCombat && !isRolling && !selecting ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button className="btn-primary w-full" style={{ fontSize: 15 }} onClick={handleStartSelecting}>
                ⚔ Start Combat
              </button>
            </div>
          ) : selecting ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button className="btn-primary w-full" style={{ fontSize: 14 }} onClick={handleConfirmSelection}>
                ⚔ Roll Initiative
              </button>
              <button className="btn-ghost btn-sm w-full" onClick={handleAutoRollSelected}>
                🎲 Auto-Roll All
              </button>
              <button className="btn-ghost btn-sm w-full" onClick={() => { setSelecting(false); cancelInitiative(); }}>
                Cancel
              </button>
            </div>
          ) : inCombat ? (
            <>
              <div style={styles.turnButtons}>
                <button className="btn-ghost btn-sm" onClick={prevTurn}>◀ Prev</button>
                <button className="btn-primary btn-sm" onClick={nextTurn}>Next ▶</button>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <button style={styles.victoryBtn} onClick={() => clearInitiative('victory')}>
                  🏆 Victory
                </button>
                <button style={styles.defeatBtn} onClick={() => clearInitiative('defeat')}>
                  💀 Defeat
                </button>
              </div>
            </>
          ) : null}

          {/* Turn timer controls — always available */}
          <TimerControls initiative={initiative} saveInitiative={saveInitiative} />

          {notInOrder.length > 0 && inCombat && (
            <div style={styles.addSection}>
              <button
                className="btn-ghost btn-sm"
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', textAlign: 'left' }}
                onClick={() => setAddOpen((v) => !v)}
              >
                <span style={styles.addLabel}>Add to order ({notInOrder.length})</span>
                <span style={{ color: '#9a8b6e' }}>{addOpen ? '▾' : '▸'}</span>
              </button>
              {addOpen && notInOrder.map((t) => (
                <button key={t.id} className="btn-ghost btn-sm" style={{ marginTop: 4, textAlign: 'left' }} onClick={() => addToInitiative(t.id)} {...hoverProps(t.id)}>
                  + {t.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}

const styles = {
  panel:          { display: 'flex', flexDirection: 'column', gap: 0, height: '100%', overflowY: 'auto', background: 'linear-gradient(180deg, #1a2a48, #142038)' },
  header:         { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #4a3d28', flexShrink: 0 },
  title:          { fontFamily: "'Press Start 2P', monospace", fontWeight: 700, fontSize: 14, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#d4a847' },
  round:          { fontSize: 12, color: '#9a8b6e', background: '#0e1528', padding: '2px 8px', borderRadius: 4 },
  list:           { listStyle: 'none', overflowY: 'auto', flex: 1, padding: '6px 0' },
  entry:          { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px 7px 8px', fontSize: 13, transition: 'background 0.1s', cursor: 'pointer', color: '#f0e6d0' },
  activeEntry:    { background: 'rgba(212,168,71,0.12)', borderLeft: '3px solid #d4a847' },
  dragOverEntry:  { background: 'rgba(212,168,71,0.18)', borderTop: '2px solid #d4a847' },
  dragHandle:     { color: '#6a5d45', fontSize: 14, cursor: 'grab', padding: '0 2px', userSelect: 'none', flexShrink: 0 },
  dot:            { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  posNum:         { fontSize: 10, color: '#6a5d45', fontWeight: 700, minWidth: 14, textAlign: 'right', flexShrink: 0 },
  entryName:      { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#f0e6d0' },
  activePip:      { color: '#d4a847', fontSize: 10 },
  defeatedBadge:  { color: '#c0392b', marginLeft: 4 },
  removeBtn:      { background: 'transparent', color: '#9a8b6e', border: 'none', fontSize: 16, lineHeight: 1, padding: '0 2px', cursor: 'pointer', borderRadius: 3 },
  removeMapBtn:   { background: 'transparent', color: '#c0392b', border: 'none', fontSize: 12, lineHeight: 1, padding: '0 2px', cursor: 'pointer', borderRadius: 3 },
  controls:       { padding: '10px 14px', borderTop: '1px solid #4a3d28', display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 },
  turnButtons:    { display: 'flex', gap: 6 },
  addSection:     { marginTop: 8, display: 'flex', flexDirection: 'column', gap: 0 },
  addLabel:       { fontFamily: "'Press Start 2P', monospace", fontSize: 11, color: '#9a8b6e', textTransform: 'uppercase', letterSpacing: '0.05em' },
  empty:          { padding: '16px 14px', color: '#9a8b6e', fontSize: 13, textAlign: 'center', flex: 1 },

  // HP editor
  hpPill:       { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, padding: '1px 4px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, color: '#f0e6d0' },
  hpMax:        { color: '#6a5d45', fontSize: 10 },
  hpPopup:      { display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 },
  hpPopupInput: { width: 44, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', fontSize: 12, padding: '2px 4px', outline: 'none', textAlign: 'center' },
  dmgBtn:       { width: 24, height: 24, background: '#3a1518', border: '1px solid #c0392b', borderRadius: 4, color: '#c0392b', fontSize: 11, fontWeight: 800, cursor: 'pointer', padding: 0, lineHeight: 1 },
  healBtn:      { width: 24, height: 24, background: '#1a3a24', border: '1px solid #5cb870', borderRadius: 4, color: '#5cb870', fontSize: 14, fontWeight: 800, cursor: 'pointer', padding: 0, lineHeight: 1 },
  cancelHpBtn:  { background: 'transparent', border: 'none', color: '#6a5d45', fontSize: 12, cursor: 'pointer', padding: '0 2px' },

  // Rolling phase
  rollList:       { flex: 1, overflowY: 'auto', padding: '8px 0' },
  rollGroup:      { marginBottom: 4 },
  rollGroupLabel: { fontFamily: "'Press Start 2P', monospace", fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9a8b6e', padding: '6px 14px 4px' },
  rollEntry:      { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderBottom: '1px solid #0e1528', fontSize: 13, color: '#f0e6d0' },
  rollName:       { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#f0e6d0' },
  rolledBadge:    { fontSize: 12, color: '#5cb870', background: 'rgba(92,184,112,0.1)', padding: '2px 8px', borderRadius: 4, flexShrink: 0 },
  waitingBadge:   { fontSize: 11, color: '#6a5d45', fontStyle: 'italic', flexShrink: 0 },
  dmRollInput:    { display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 },
  rollInput:      { width: 52, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', padding: '5px 6px', fontSize: 13, outline: 'none', textAlign: 'center' },
  submitRollBtn:  { width: 28, height: 28, background: '#5cb870', border: 'none', borderRadius: 4, color: '#0a0f1e', fontSize: 14, fontWeight: 800, cursor: 'pointer', padding: 0 },
  autoRollBtn:    { width: 28, height: 28, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', fontSize: 14, cursor: 'pointer', padding: 0 },
  victoryBtn:     { flex: 1, background: '#1a3a24', border: '1px solid #4ade80', borderRadius: 6, color: '#4ade80', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '8px 0', letterSpacing: '0.03em' },
  defeatBtn:      { flex: 1, background: '#3a1518', border: '1px solid #c0392b', borderRadius: 6, color: '#c0392b', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '8px 0', letterSpacing: '0.03em' },

  // Selected token highlight
  selectedEntry:  { background: 'rgba(96,165,250,0.15)', borderLeft: '3px solid #60a5fa' },

  // Token selection phase
  selectBody:     { flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 },
  selectHeader:   { fontFamily: "'Press Start 2P', monospace", fontSize: 12, fontWeight: 700, color: '#d4a847', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' },
  selectCount:    { fontSize: 13, color: '#f0e6d0', fontWeight: 700, textAlign: 'center', padding: '4px 0' },
  selectListBody: { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 },
  selectRow:      { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', background: '#0e1528', borderRadius: 4, cursor: 'pointer', fontSize: 12, color: '#f0e6d0' },
  selectName:     { flex: 1 },
  selectRemove:   { color: '#6a5d45', fontSize: 12, flexShrink: 0 },
};
